import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LifeBuoy, X, Send, Loader2, CheckCircle2, MessageCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Floating support bubble.
 *
 * Anchored bottom-right on every dashboard page. Click to open a compact
 * message form that posts to `POST /api/support/message` (Telegram-forwarded
 * on the backend).
 */
export default function SupportBubble() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-sky-500 hover:bg-sky-600 shadow-lg shadow-sky-500/40 text-white flex items-center justify-center transition-transform hover:scale-105"
        aria-label="Get help"
        data-testid="support-bubble-btn"
        title="Get help"
      >
        <LifeBuoy className="h-6 w-6" />
      </button>
      {open && <SupportPanel onClose={() => setOpen(false)} />}
    </>
  );
}

function SupportPanel({ onClose }) {
  const { user } = useAuth();
  const location = useLocation();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || user?.business_name || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // { ticket_id, delivered } | null

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!subject.trim() || !message.trim()) {
      toast.error("Please add a subject and a message.");
      return;
    }
    if (!email.trim()) {
      toast.error("Please add an email so we can reply.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/support/message", {
        subject: subject.trim(),
        message: message.trim(),
        email: email.trim(),
        name: name.trim(),
        category,
        page_url: typeof window !== "undefined" ? window.location.href : location.pathname,
      });
      setSent(r.data);
      toast.success(`Message sent · ticket ${r.data.ticket_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-3 sm:p-6" data-testid="support-panel">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full sm:w-[420px] max-h-[92vh] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-br from-sky-50 to-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-sky-500 text-white flex items-center justify-center">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-900">Need a hand?</div>
              <div className="text-[11px] text-slate-500">Usually replies within 30 minutes</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
            aria-label="Close"
            data-testid="support-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {sent ? (
          <div className="p-6 text-center flex-1">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <div className="text-base font-bold text-slate-900">Ticket {sent.ticket_id} · sent</div>
            <div className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
              {sent.delivered
                ? "We got your message. You'll hear from us at "
                : "We stored your message and will process it soon. We'll reply at "}
              <span className="font-medium text-slate-700">{email}</span>.
            </div>
            <div className="flex gap-2 mt-5 justify-center">
              <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
              <Link to="/dashboard/support" onClick={onClose}>
                <Button className="rounded-full bg-sky-500 hover:bg-sky-600">Open Help Center</Button>
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 flex-1 overflow-y-auto space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Your name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Baker"
                  className="mt-1 h-9 text-sm"
                  data-testid="support-name-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Email *</Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  className="mt-1 h-9 text-sm"
                  data-testid="support-email-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="support-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General question</SelectItem>
                  <SelectItem value="bug">Something isn&apos;t working</SelectItem>
                  <SelectItem value="setup">TV / Stick setup</SelectItem>
                  <SelectItem value="design">Design / template help</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="feature">Feature request</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Subject *</Label>
              <Input
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. TV shows blank white screen"
                className="mt-1 h-9 text-sm"
                data-testid="support-subject-input"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Message *</Label>
              <textarea
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's happening. If you can, mention the screen name or design you're on."
                className="mt-1 w-full min-h-[120px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400"
                data-testid="support-message-input"
              />
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full h-11 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-sm"
              data-testid="support-send-btn"
            >
              {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : <><Send className="h-4 w-4 mr-2" />Send message</>}
            </Button>
            <div className="pt-1 text-center">
              <Link
                to="/dashboard/support"
                onClick={onClose}
                className="text-[11px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              >
                Open full Help Center
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
